import React, { memo } from "react";

import ManualPlayerBadge from "@/components/molecules/ManualPlayerBadge";
import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
// eslint-disable-next-line no-restricted-imports -- RFC-#6 genuine-runtime: unavoidable primitive usage outside atoms (@/components/ui import(s): @/components/ui/box) —…
import { Box as UIBox } from "@/components/ui/box";

/**
 *
 */
interface SidebarPlayerCardViewProps {
  playerName: string;
  simplifiedValue: string;
  contractMonths: number | null;
  teamName: string;
  age: number;
  archetype: string;
  gpr: number;
  _isHovered?: boolean;
  isManual?: boolean;
  isOnKanbanBoard?: boolean;
  kanbanPhaseName?: string;
  /**
   * Everything the card's outer Box needs, assembled by the Container: the
   * dnd-kit node ref, its listeners and attributes, the drag/hover style, and
   * the press handlers — all suppressed when the player is already on the
   * board.
   */
  boxProps: Record<string, unknown>;
  renderBody?: () => React.ReactNode;
}

/**
 * View component for sidebar player card with dnd-kit drag integration
 * @param root0 - Component props
 * @param root0.playerName - Full name of the player
 * @param root0.simplifiedValue - Formatted contract value string with currency symbol
 * @param root0.contractMonths - Contract duration in months
 * @param root0.teamName - Name of player's current team
 * @param root0.age - Player's age in years
 * @param root0.archetype - Player archetype or position classification
 * @param root0.gpr - Gemini Performance Rating score
 * @param root0.isManual - Whether player was manually created (shows badge)
 * @param root0.isOnKanbanBoard - Whether player is already on kanban board (disables drag)
 * @param root0.boxProps - The outer Box's ref, listeners, style and handlers
 * @param root0.kanbanPhaseName - Name of kanban phase player is in (shown when on board)
 * @param root0.renderBody - Optional replacement contents; the supplied body owns its own insets (SE-6554)
 */
const SidebarPlayerCardView = ({
  playerName,
  simplifiedValue,
  contractMonths,
  teamName,
  age,
  archetype,
  gpr,
  isManual = false,
  isOnKanbanBoard = false,
  kanbanPhaseName,
  boxProps,
  renderBody,
}: SidebarPlayerCardViewProps) => (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Box web version doesn't support React Native event props
  <UIBox {...(boxProps as any)}>
    {renderBody ? (
      renderBody()
    ) : (
      <Stack space="2">
        <Stack direction="horizontal" className="items-start justify-between">
          <Stack className="flex-1 gap-micro">
            <Stack direction="horizontal" className="items-center gap-1">
              <Text variant="body-strong" className="text-content-primary">
                {playerName}
              </Text>
              <ManualPlayerBadge show={isManual} compact />
            </Stack>
            <Text variant="caption" className="text-content-muted">
              {archetype} • {age}y
            </Text>
            <Text variant="caption" className="text-content-muted">
              {teamName}
            </Text>
            {isOnKanbanBoard && kanbanPhaseName && (
              <Text
                variant="unstyled"
                className="font-medium text-caption text-status-success"
              >
                Currently in {kanbanPhaseName}
              </Text>
            )}
          </Stack>
          <Stack className="items-end gap-px">
            <Text variant="body-strong" className="text-content-primary">
              {simplifiedValue}
            </Text>
            <Text variant="caption" className="text-content-subtle">
              {contractMonths !== null ? `${contractMonths}mo` : "N/A"}
            </Text>
            <Text variant="caption" className="text-content-muted">
              GPR {gpr}
            </Text>
          </Stack>
        </Stack>

        <Box className="h-px bg-outline-subtle opacity-50" />
      </Stack>
    )}
  </UIBox>
);

SidebarPlayerCardView.displayName = "SidebarPlayerCardView";

export default memo(SidebarPlayerCardView);
